//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import * as instagramMentionedAndTaggedApi from '@apis/instagram-mentioned-and-tagged/InstagramMentionedAndTaggedApi';
import {
  IInstagramMentionedAndTaggedMediaData,
  IInstagramMentionedMediaStats,
} from '@apis/instagram-mentioned-and-tagged/InstagramMentionedAndTaggedApiModel';
import {
  IAliasedDateRange,
  getAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import {
  generateInstagramSearchRequestBody,
  generateMultiHandleInstagramSearchRequestBody,
  transformAutocompleteData,
} from '@instagram-account/instagramSharedUtils';
import { IInstagramSourceAndCountWithAutocomplete } from '@instagram-account/models/IInstagramAutocompleteResponses';
import {
  IInstagramMentionedAndTaggedState,
  INSTAGRAM_MENTIONED_AND_TAGGED_LIMIT,
} from '@instagram-account/redux/mentioned-and-tagged/InstagramMentionedAndTaggedModel';
import { getDefaultInstagramAccountUsername } from '@instagram-account/redux/selectors/getDefaultInstagramAccountInfo';
import { getHasMentionedAndTaggedRecommendedSortingFeatureEnabled } from '@instagram-account/selectors/getMentionedAndTaggedRecommendedSorting';
import { FilterMatchState } from '@models/FilterMatchState';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import InstagramUsername from '@models/InstagramUsername';
import TimeZoneId from '@models/TimeZoneId';
import { UniversalSearchSortKey } from '@models/UniversalSearchSortKey';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

import { crl8 } from 'app/curalate.globals';

const initialState: IInstagramMentionedAndTaggedState = {
  autocompleteResults: [],
  contentStats: {
    avgComments: 0,
    avgLikes: 0,
    comments: 0,
    likes: 0,
  },
  dateRange: getAliasedDateRange(
    'last90Days',
    crl8.session?.timeZone.id || TimeZoneId.Utc,
  ),
  errors: {
    fetchInstagramMentionedAndTaggedAutocompleteError: undefined,
    fetchInstagramMentionedAndTaggedContentError: undefined,
    fetchMoreInstagramMentionedAndTaggedContentError: undefined,
  },
  flags: {
    isEndOfInstagramMentionedAndTaggedContentReached: false,
    isFetchingInstagramMentionedAndTaggedAutocomplete: false,
    isFetchingInstagramMentionedAndTaggedContent: false,
    isFetchingMoreInstagramMentionedAndTaggedContent: false,
  },
  items: [],
  offset: 0,
  searchTerms: [],
  sort: UniversalSearchSortKey.PostedTimestamp,
  totalMediaCount: 0,
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramMentionedAndTagged,
  initialState,
  reducers: {
    setInstagramMentionedAndTaggedDateRange(
      state,
      action: PayloadAction<IAliasedDateRange>,
    ) {
      state.dateRange = action.payload;
    },
    addInstagramMentionedAndTaggedSearchTerm(
      state,
      action: PayloadAction<IInstagramSourceAndCountWithAutocomplete>,
    ) {
      state.searchTerms = [...state.searchTerms, action.payload];
    },
    setInstagramMentionedAndTaggedSearchTerms(
      state,
      action: PayloadAction<IInstagramSourceAndCountWithAutocomplete[]>,
    ) {
      state.searchTerms = [...action.payload];
    },
    setInstagramMentionedAndTaggedSort(
      state,
      action: PayloadAction<UniversalSearchSortKey>,
    ) {
      state.sort = action.payload;
    },
    requestInstagramMentionedAndTaggedContent(state) {
      state.flags.isFetchingInstagramMentionedAndTaggedContent = true;
    },
    receiveInstagramMentionedAndTaggedContentSucceed(
      state,
      action: PayloadAction<IInstagramMentionedAndTaggedMediaData>,
    ) {
      state.flags.isEndOfInstagramMentionedAndTaggedContentReached =
        action.payload.media.length < INSTAGRAM_MENTIONED_AND_TAGGED_LIMIT;
      state.flags.isFetchingInstagramMentionedAndTaggedContent = false;
      state.items = [...action.payload.media];
      state.offset = action.payload.offset;
      state.contentStats = action.payload.stats;
      state.totalMediaCount = action.payload.totalCount;
    },
    receiveInstagramMentionedAndTaggedFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingInstagramMentionedAndTaggedContent = false;
      state.errors.fetchInstagramMentionedAndTaggedContentError =
        action.payload;
    },
    requestMoreInstagramMentionedAndTaggedContent(state) {
      state.flags.isFetchingMoreInstagramMentionedAndTaggedContent = true;
    },
    receiveMoreInstagramMentionedAndTaggedContentSucceed(
      state,
      action: PayloadAction<{
        content: IMlsMediaItem[];
        offset: number;
        stats: IInstagramMentionedMediaStats;
      }>,
    ) {
      state.flags.isEndOfInstagramMentionedAndTaggedContentReached =
        action.payload.content.length < INSTAGRAM_MENTIONED_AND_TAGGED_LIMIT;
      state.flags.isFetchingMoreInstagramMentionedAndTaggedContent = false;
      state.items = [...state.items, ...action.payload.content];
      state.offset = action.payload.offset;
      state.contentStats = action.payload.stats;
    },
    receiveMoreInstagramMentionedAndTaggedFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingMoreInstagramMentionedAndTaggedContent = false;
      state.errors.fetchMoreInstagramMentionedAndTaggedContentError =
        action.payload;
    },
    requestInstagramMentionedAndTaggedAutocomplete(state) {
      state.flags.isFetchingInstagramMentionedAndTaggedAutocomplete = true;
    },
    receiveInstagramMentionedAndTaggedAutocompleteSucceed(
      state,
      action: PayloadAction<IInstagramSourceAndCountWithAutocomplete[]>,
    ) {
      state.flags.isFetchingInstagramMentionedAndTaggedAutocomplete = false;
      state.autocompleteResults = [...action.payload];
    },
    receiveInstagramMentionedAndTaggedAutocompleteFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingInstagramMentionedAndTaggedAutocomplete = false;
      state.errors.fetchInstagramMentionedAndTaggedAutocompleteError =
        action.payload;
    },
    resetContentState(state) {
      state.offset = 0;
      state.flags.isEndOfInstagramMentionedAndTaggedContentReached = false;
      state.items = [];
    },
    resetMentionedAndTaggedState(state) {
      return initialState;
    },
  },
});

const getMentionedAndTaggedContent = createThunk(
  (instagramUsername: InstagramUsername) => async (dispatch, getState) => {
    const { dateRange, offset, searchTerms, sort } =
      getState().instagram.mentionedAndTagged;

    const searchedBody = generateInstagramSearchRequestBody(searchTerms);

    return instagramMentionedAndTaggedApi.getMentionedAndTaggedMedia(
      {
        mentionedUsername: instagramUsername,
        matchState: FilterMatchState.All,
        startTime: dateRange.startDate,
        endTime: dateRange.endDate,
        offset,
        limit: INSTAGRAM_MENTIONED_AND_TAGGED_LIMIT,
        sortKey: sort,
      },
      searchedBody,
    );
  },
);

export const fetchInstagramMentionedAndTaggedContent = createThunk(
  () => async (dispatch, getState) => {
    const {
      flags: { isFetchingInstagramMentionedAndTaggedContent },
    } = getState().instagram.mentionedAndTagged;
    const instagramUsername = getDefaultInstagramAccountUsername(getState());

    if (!instagramUsername || isFetchingInstagramMentionedAndTaggedContent) {
      return;
    }

    dispatch(actions.resetContentState());
    dispatch(actions.requestInstagramMentionedAndTaggedContent());

    try {
      const mentionedAndTaggedData = await dispatch(
        getMentionedAndTaggedContent(instagramUsername),
      );

      dispatch(
        actions.receiveInstagramMentionedAndTaggedContentSucceed(
          mentionedAndTaggedData.data,
        ),
      );
    } catch (error) {
      dispatch(
        actions.receiveInstagramMentionedAndTaggedFailed(
          getRequestErrorMessage(error),
        ),
      );
    }
  },
);

// Multi handle support

export const getMultiHandleMentionedAndTaggedContent = createThunk(
  (mentionedUsrnames: string[]) => async (dispatch, getState) => {
    const { dateRange, offset, searchTerms, sort } =
      getState().instagram.mentionedAndTagged;
    const searchedBody = generateMultiHandleInstagramSearchRequestBody(
      searchTerms,
      mentionedUsrnames,
    );
    return instagramMentionedAndTaggedApi.getMultiHandleMentionedAndTaggedMedia(
      {
        matchState: FilterMatchState.All,
        startTime: dateRange.startDate,
        endTime: dateRange.endDate,
        offset,
        limit: INSTAGRAM_MENTIONED_AND_TAGGED_LIMIT,
        sortKey: sort,
      },
      searchedBody,
    );
  },
);

export const fetchMultiHandleInstagramMentionedAndTaggedContent = createThunk(
  (mentionedUsrnames: string[]) => async (dispatch, getState) => {
    const {
      flags: { isFetchingInstagramMentionedAndTaggedContent },
    } = getState().instagram.mentionedAndTagged;
    const instagramUsername = getDefaultInstagramAccountUsername(getState());
    if (!instagramUsername || isFetchingInstagramMentionedAndTaggedContent) {
      return;
    }
    dispatch(actions.resetContentState());
    dispatch(actions.requestInstagramMentionedAndTaggedContent());
    try {
      const mentionedAndTaggedData = await dispatch(
        getMultiHandleMentionedAndTaggedContent(mentionedUsrnames),
      );
      dispatch(
        actions.receiveInstagramMentionedAndTaggedContentSucceed(
          mentionedAndTaggedData.data,
        ),
      );
    } catch (error) {
      dispatch(
        actions.receiveInstagramMentionedAndTaggedFailed(
          getRequestErrorMessage(error),
        ),
      );
    }
  },
);
export const fetchMoreInstagramMentionedAndTaggedContent = createThunk(
  () => async (dispatch, getState) => {
    const {
      flags: {
        isEndOfInstagramMentionedAndTaggedContentReached,
        isFetchingInstagramMentionedAndTaggedContent,
        isFetchingMoreInstagramMentionedAndTaggedContent,
      },
    } = getState().instagram.mentionedAndTagged;
    const instagramUsername = getDefaultInstagramAccountUsername(getState());

    if (
      !instagramUsername ||
      isEndOfInstagramMentionedAndTaggedContentReached ||
      isFetchingInstagramMentionedAndTaggedContent ||
      isFetchingMoreInstagramMentionedAndTaggedContent
    ) {
      return;
    }

    dispatch(actions.requestMoreInstagramMentionedAndTaggedContent());

    try {
      const mentionedAndTaggedData = await dispatch(
        getMentionedAndTaggedContent(instagramUsername),
      );

      dispatch(
        actions.receiveMoreInstagramMentionedAndTaggedContentSucceed({
          content: mentionedAndTaggedData.data.media,
          stats: mentionedAndTaggedData.data.stats,
          offset: mentionedAndTaggedData.data.offset,
        }),
      );
    } catch (error) {
      dispatch(
        actions.receiveMoreInstagramMentionedAndTaggedFailed(
          getRequestErrorMessage(error),
        ),
      );
    }
  },
);

export const setDateRangeAndRefreshContent = createThunk(
  (dateRange: IAliasedDateRange, selectedConnectedUsers?: string[]) =>
    async (dispatch, getState) => {
      dispatch(actions.setInstagramMentionedAndTaggedDateRange(dateRange));
      if (selectedConnectedUsers) {
        dispatch(
          fetchMultiHandleInstagramMentionedAndTaggedContent(
            selectedConnectedUsers,
          ),
        );
      } else {
        dispatch(fetchInstagramMentionedAndTaggedContent());
      }
    },
);

export const setFilterAndRefreshContent = createThunk(
  (
    filterOption: IInstagramSourceAndCountWithAutocomplete,
    selectedConnectedUsers?: string[],
  ) =>
    async (dispatch, getState) => {
      const { searchTerms } = getState().instagram.mentionedAndTagged;

      const hasSearchTerm = searchTerms.some(
        (term) => term.source === filterOption.source,
      );

      if (hasSearchTerm) {
        return;
      }

      dispatch(actions.addInstagramMentionedAndTaggedSearchTerm(filterOption));
      if (selectedConnectedUsers) {
        dispatch(
          fetchMultiHandleInstagramMentionedAndTaggedContent(
            selectedConnectedUsers,
          ),
        );
      } else {
        dispatch(fetchInstagramMentionedAndTaggedContent());
      }
    },
);

export const setSortAndRefreshContent = createThunk(
  (sort?: UniversalSearchSortKey, selectedConnectedUsers?: string[]) =>
    async (dispatch, getState) => {
      const isMentionedAndTaggedRecommendedSortingFeatureEnabled =
        getHasMentionedAndTaggedRecommendedSortingFeatureEnabled(getState());

      const { sort: initialSort } = getState().instagram.mentionedAndTagged;

      const defaultSortKey =
        isMentionedAndTaggedRecommendedSortingFeatureEnabled
          ? UniversalSearchSortKey.SimilarityToActedOnScore
          : initialSort;

      const sortKey = isDefined(sort) ? sort : defaultSortKey;

      dispatch(actions.setInstagramMentionedAndTaggedSort(sortKey));
      if (selectedConnectedUsers?.length) {
        dispatch(
          fetchMultiHandleInstagramMentionedAndTaggedContent(
            selectedConnectedUsers,
          ),
        );
      } else {
        dispatch(fetchInstagramMentionedAndTaggedContent());
      }
    },
);

export const removeFilterByIndexAndRefreshContent = createThunk(
  (index: number) => async (dispatch, getState) => {
    const { searchTerms } = getState().instagram.mentionedAndTagged;

    const updatedSearchTerms = [
      ...searchTerms.slice(0, index),
      ...searchTerms.slice(index + 1),
    ];

    dispatch(
      actions.setInstagramMentionedAndTaggedSearchTerms(updatedSearchTerms),
    );
    dispatch(fetchInstagramMentionedAndTaggedContent());
  },
);

export const removeAllFiltersAndRefreshContent = createThunk(
  () => async (dispatch, getState) => {
    dispatch(actions.setInstagramMentionedAndTaggedSearchTerms([]));
    dispatch(fetchInstagramMentionedAndTaggedContent());
  },
);

export const fetchMentionedAndTaggedAutocomplete = createThunk(
  (searchTerm: string = '') =>
    async (dispatch, getState) => {
      const { dateRange, searchTerms } =
        getState().instagram.mentionedAndTagged;
      const instagramUsername = getDefaultInstagramAccountUsername(getState());

      if (!instagramUsername) {
        return;
      }

      dispatch(actions.requestInstagramMentionedAndTaggedAutocomplete());

      try {
        const searchedBody = generateInstagramSearchRequestBody(searchTerms);

        const autocompleteData =
          await instagramMentionedAndTaggedApi.getMentionedAndTaggedAutocompleteSuggestions(
            {
              mentionedUsername: instagramUsername,
              searchTerm,
              matchState: FilterMatchState.All,
              startTime: dateRange.startDate,
              endTime: dateRange.endDate,
            },
            searchedBody,
          );

        const autocompleteResults = transformAutocompleteData(
          autocompleteData.data,
        );

        dispatch(
          actions.receiveInstagramMentionedAndTaggedAutocompleteSucceed(
            autocompleteResults,
          ),
        );
      } catch (error) {
        dispatch(
          actions.receiveInstagramMentionedAndTaggedAutocompleteFailed(
            getRequestErrorMessage(error),
          ),
        );
      }
    },
);

export const generateMentionedAndTaggedSummary = createThunk(
  (instagramUsername: InstagramUsername) => async (dispatch, getState) => {
    const { dateRange, searchTerms, sort } =
      getState().instagram.mentionedAndTagged;

    const searchedBody = generateInstagramSearchRequestBody(searchTerms);

    return instagramMentionedAndTaggedApi.getMentionedAndTaggedContentZip(
      {
        mentionedUsername: instagramUsername,
        matchState: FilterMatchState.All,
        startTime: dateRange.startDate,
        endTime: dateRange.endDate,
        sortKey: sort,
      },
      searchedBody,
    );
  },
);

export const { resetMentionedAndTaggedState } = actions;

export default reducer;
